/**
 * @file app-project.ts
 *
 * Project and session management for as-app: event handlers, autosave,
 * project CRUD helpers, and the session visibility/freeze/thaw handler.
 * All exported functions take a ProjectContext as their first argument.
 */

import {
  createNewProject,
  markProjectSaved,
  serializeProject,
  deserializeProject,
  currentProjectData,
  type Project,
  applyMigrations,
} from '../../data/projectManagement.ts';
import { liveAppData, clearUiInputs } from '../../store/store.ts';
import {
  storeProject,
  getProjectsForApp,
  getProjectById,
  deleteProject,
  updateProject,
} from '../../logic/db.ts';
import { devlog } from '../../logic/utils.ts';
import { saveFileAs } from '../../logic/fileSave.ts';
import type { AsModalOverlay } from '../overlays/as-modal-overlay.ts';
import type { AsModalSaveProject } from '../overlays/as-modal-save-project.ts';
import type { AsConfigurator } from '../paramate/as-configurator.ts';

// ============================================================================
// Context interface — supplied by AsApp
// ============================================================================

export interface ProjectContext {
  readonly appId: string;
  readonly simpleProjects: boolean;
  readonly version: string;
  unsavedChanges: boolean;
  markPristine(): void;
  isSaving: boolean;
  pendingAction: 'new-session' | 'home' | 'save-and-close' | null;
  currentProject: Project | null;
  frozenAt: number | null;
  sessionSnapshotNeeded: boolean;
  autoSaveDebounceTimer: number | null;
  hasAttribute(name: string): boolean;
  querySelector<T extends Element>(selector: string): T | null;
  dispatchEvent(ev: Event): boolean;
  readonly modalOverlay: AsModalOverlay | null;
  logError(origin: string, err: unknown, importance?: 'critical' | 'regular' | 'warning', label?: string): void;
  showErrorModal(detail: { error: Error | string; origin?: string; info?: string }): void;
  openModal(id: string, tag: string, attrs?: Record<string, string>): void;
  clearHistory(): void;
  /** Run registered before-save hooks (awaited, in order) — flushes app state into customData. */
  runBeforeSaveHooks(): Promise<void>;
}

// ============================================================================
// Autosave
// ============================================================================

// Autosave to IDB is disabled to prevent untitled projects from piling up,
// but we STILL run an in-memory capture so that `ctx.currentProject.paramateSession`
// stays up-to-date. This is required to restore the session if the computer goes to sleep!
export const scheduleAutoSave = (ctx: ProjectContext): void => {
  if (ctx.autoSaveDebounceTimer) clearTimeout(ctx.autoSaveDebounceTimer);
  ctx.autoSaveDebounceTimer = window.setTimeout(async () => {
    if (!ctx.currentProject) return;
    await captureProjectState(ctx, 'IN-MEMORY-AUTOSAVE');
    ctx.currentProject.metadata.updatedAt = new Date();
    // IDB save is skipped here.
    ctx.sessionSnapshotNeeded = false;
  }, 2000);
};

// Set once configuratorEl.storeSession() has failed — stops captureProjectState
// from calling it again for the rest of the page's lifetime. Without this, a
// session that can't be captured (expired, or the environment's token simply
// isn't authorized for it) gets retried on every single edit forever, each
// attempt logging a fresh, unavoidable console error from the Paramate SDK
// itself (it broadcasts every failed request globally, independent of this
// catch). One failure is enough to know retrying won't help.
let sessionCaptureBroken = false;

/**
 * Flush the app's latest state onto `ctx.currentProject` ahead of
 * serialization/storage — the single collect point shared by every save path
 * (`saveCurrentProject`, `autoSaveToIdb`):
 *   1. run before-save hooks so apps can write their latest state into customData,
 *   2. capture the live paramate session (→ `currentProject.paramateSession`),
 *   3. snapshot `uiInputs` + `customData` onto `currentProject`.
 * Hook/session failures are logged with the caller's `tag` but never abort the
 * save. The caller must have already verified `ctx.currentProject` is set.
 */
const captureProjectState = async (ctx: ProjectContext, tag: string): Promise<void> => {
  // Let apps flush their latest state into customData before we read it.
  try { await ctx.runBeforeSaveHooks(); }
  catch (err) { console.warn(`[${tag}] before-save hook failed:`, err); }

  const configuratorEl = ctx.querySelector<AsConfigurator>('as-configurator');
  if (configuratorEl?.initialized && !sessionCaptureBroken) {
    try { await configuratorEl.storeSession(); }
    catch (err) {
      console.warn(`[${tag}] Could not capture paramate session — giving up on session capture for this page load:`, err);
      sessionCaptureBroken = true;
    }
  }

  ctx.currentProject!.uiInputs = { ...liveAppData.uiInputs };
  ctx.currentProject!.customData = { ...currentProjectData.customData };
};

const autoSaveToIdb = async (ctx: ProjectContext): Promise<void> => {
  if (!ctx.currentProject) return;
  await captureProjectState(ctx, 'AUTOSAVE');
  ctx.currentProject.metadata.updatedAt = new Date();
  await storeProject(ctx.currentProject);
  liveAppData.currentProject = ctx.currentProject;
  ctx.sessionSnapshotNeeded = false;
  devlog('[AUTOSAVE] IDB autosave complete');
};

// ============================================================================
// Project helpers
// ============================================================================

export const refreshProjectsList = async (ctx: ProjectContext): Promise<void> => {
  const projects = await getProjectsForApp(ctx.appId);
  liveAppData.savedProjects = projects;
  ctx.dispatchEvent(new CustomEvent('as-projects-list', { bubbles: true, detail: { projects } }));
};

export const createAndSetNewProject = (ctx: ProjectContext): void => {
  currentProjectData.setCustomData({}, true);
  ctx.currentProject = createNewProject(ctx.appId);
  liveAppData.currentProject = ctx.currentProject;
  // Settle clean: the as-reset-configurator below triggers an async configure
  // whose param-sync re-dirties after this turn, so a one-shot clear wouldn't stick.
  ctx.markPristine();
  clearUiInputs();

  // First, reset all inputs to their default states
  window.dispatchEvent(new CustomEvent('as-inputs-reset', { detail: { silenceChange: true } }));

  // Then, reset the configurator and history explicitly
  window.dispatchEvent(new CustomEvent('as-reset-configurator'));

  // Reset step selector to initial value (bypassing any locks).
  // silenceChange: true prevents app step-change handlers from firing a configure
  // cycle that would push spurious history entries before the stack is cleared.
  window.dispatchEvent(new CustomEvent('as-force-step', { detail: { stepIndex: 0, silenceChange: true } }));

  // Then, notify that a new project has been created
  ctx.dispatchEvent(new CustomEvent('as-project-new', { bubbles: true, detail: { project: ctx.currentProject } }));

  ctx.dispatchEvent(new CustomEvent('as-notify', {
    detail: { defaultText: 'New project created', dictKey: 'notifications.project.new', type: 'info' },
  }));

  // Defer so any async configurator reset work (as-reset-configurator) has settled
  setTimeout(() => ctx.clearHistory(), 0);
};

export const loadProject = async (ctx: ProjectContext, project: Project): Promise<void> => {
  window.dispatchEvent(new CustomEvent('as-app-busy-request', { detail: { key: 'load-project', busy: true } }));
  try {
    // Reset step selector to initial value (bypassing any locks) before we start restoring.
    // This prevents step-change side effects (like configure calls) from interrupting 
    // the asynchronous model reloading during restoreSession.
    window.dispatchEvent(new CustomEvent('as-force-step', { 
      detail: { 
        stepIndex: 0,
        silenceChange: !!project.paramateSession 
      } 
    }));

    const configuratorEl = ctx.querySelector<AsConfigurator>('as-configurator');

    let currentSessionBlob: Blob | undefined;
    if (configuratorEl?.initialized && project.paramateSession) {
      try { currentSessionBlob = await configuratorEl.getSessionBlob(); } catch { /* skip merge */ }
    }
    try {
      project = await applyMigrations(project, ctx.version, currentSessionBlob);
    } catch (err) {
      console.warn('[LOAD] Session migration failed — loading without session merge:', err);
      project = { ...project, paramateSession: undefined };
    }

    ctx.currentProject = project;
    liveAppData.currentProject = project;
    liveAppData.uiInputs = { ...project.uiInputs };
    currentProjectData.uiInputs = { ...project.uiInputs };
    currentProjectData.setCustomData(project.customData ?? {}, true);

    if (project.paramateSession && configuratorEl) {
      await new Promise<void>(resolve => {
        const done = () => {
          window.removeEventListener('paramate-configurator-after-restore', done);
          window.removeEventListener('as-session-restore-failed', done);
          resolve();
        };
        window.addEventListener('paramate-configurator-after-restore', done);
        window.addEventListener('as-session-restore-failed', done);
        configuratorEl.restoreSession(project.paramateSession!);
        devlog('[LOAD] Restoring paramate session, waiting for after-restore…');
      });
      devlog('[LOAD] Paramate session restore complete');
    }

    if (Object.keys(project.uiInputs).length > 0) {
      window.dispatchEvent(new CustomEvent('as-restore-input-values', { 
        detail: { 
          inputs: project.uiInputs,
          silent: !!project.paramateSession 
        } 
      }));
    }

    ctx.dispatchEvent(new CustomEvent('session-restored', {
      bubbles: true, composed: true, detail: { customData: currentProjectData.customData },
    }));

    // restored 
    ctx.dispatchEvent(new CustomEvent('as-project-loaded', { bubbles: true, detail: { project } }));

    // Settle clean: a restore triggers an async configure that re-dirties after
    // this turn, so clear through the settle window rather than once.
    ctx.markPristine();
    ctx.clearHistory();
    ctx.dispatchEvent(new CustomEvent('as-notify', {
      detail: { defaultText: `Loaded project: ${project.metadata.name}`, dictKey: 'notifications.project.loaded', type: 'info' },
    }));

  } finally {
    window.dispatchEvent(new CustomEvent('as-app-busy-request', { detail: { key: 'load-project', busy: false } }));
  }
};

/**
 * Save the current project via the regular file-save method (saveFileAs).
 * Returns true only when a file was actually written; false when there's nothing
 * to save, a save is already running, or the user cancelled the save dialog — so
 * callers can gate "save-and-close / save-and-home" on a real save and never
 * proceed (and lose work) on a cancel.
 */
export const saveCurrentProject = async (ctx: ProjectContext, name?: string): Promise<boolean> => {
  devlog('[SAVE] saveCurrentProject: entry — currentProject:', !!ctx.currentProject, '| isSaving:', ctx.isSaving, '| appEnv:', liveAppData.appEnv ?? '(none)');
  if (!ctx.currentProject) { devlog('[SAVE] saveCurrentProject: no currentProject, abort'); return false; }
  if (ctx.isSaving) { devlog('[SAVE] Already saving, skipping duplicate call'); return false; }
  ctx.isSaving = true;
  try {
    if (name) ctx.currentProject.metadata.name = name;

    await captureProjectState(ctx, 'SAVE');

    const blob = await serializeProject(ctx.currentProject);
    const _appName = ((ctx.querySelector('as-header') as HTMLElement | null)?.getAttribute('app-title') ?? ctx.appId).replace(/\s+/g, '');
    const filename = `${ctx.currentProject.metadata.name}.${_appName}.asproject`;

    const result = await saveFileAs(blob, filename, {
      title: 'Save Project',
      fileExtension: 'asproject',
      fileExtensionName: 'App Suite Project',
    });
    if (!result.success) {
      devlog('[SAVE] Save cancelled by user');
      return false;
    }
    devlog('[SAVE] Written:', filename, result.filepath ?? '(via download)');

    ctx.currentProject = markProjectSaved(ctx.currentProject);
    // IDB persistence disabled — file download is the only save path for now.
    // await storeProject(ctx.currentProject);
    liveAppData.currentProject = ctx.currentProject;
    ctx.unsavedChanges = false;

    // await refreshProjectsList(ctx);

    ctx.dispatchEvent(new CustomEvent('as-project-saved'));
    ctx.dispatchEvent(new CustomEvent('as-notify', {
      detail: { defaultText: `Project saved: ${ctx.currentProject.metadata.name}`, dictKey: 'notifications.project.saved', type: 'success' },
    }));
    return true;
  } finally {
    ctx.isSaving = false;
  }
};

// ============================================================================
// Modal helpers
// ============================================================================

export const openNewProjectWarningModal = (ctx: ProjectContext): void => {
  ctx.openModal('unsaved-warning', 'as-modal-unsaved-warning', { mode: 'new' });
  ctx.modalOverlay?.querySelector('#unsaved-warning')?.removeAttribute('saved');
};

export const openSaveProjectModal = (ctx: ProjectContext): void => {
  const saveModal = ctx.modalOverlay?.querySelector('#save-project') as AsModalSaveProject | null;
  if (saveModal) {
    if (ctx.currentProject?.metadata.name && ctx.currentProject.metadata.name !== 'Untitled') {
      saveModal.setValue(ctx.currentProject.metadata.name);
    } else {
      saveModal.reset();
    }
  }
  ctx.openModal('save-project', 'as-modal-save-project');
  setTimeout(() => {
    (ctx.modalOverlay?.querySelector('#save-project') as AsModalSaveProject | null)?.focusInput();
  }, 100);
};

export const closeSaveProjectModal = (ctx: ProjectContext): void => {
  if (ctx.modalOverlay?.hasAttribute('show')) ctx.modalOverlay.close();
};

// ============================================================================
// Event handlers
// ============================================================================

export const handleNewSession = (ctx: ProjectContext): void => {
  if (ctx.hasAttribute('disable-session-handlers')) return;
  if (!ctx.simpleProjects && ctx.unsavedChanges) {
    ctx.pendingAction = 'new-session';
    openNewProjectWarningModal(ctx);
  } else {
    createAndSetNewProject(ctx);
  }
};

/**
 * Hosts running with disable-session-handlers own the actual save. Hand the
 * request to them via the documented hook: an `as-button-click` with
 * buttonName 'save-and-close', dispatched from <as-export-step> (or as-app
 * itself when no export step exists) so listeners at any ancestor catch it.
 */
export const delegateSaveToHost = (ctx: ProjectContext): void => {
  const exportStep = document.querySelector('as-export-step') ?? ctx;
  exportStep.dispatchEvent(new CustomEvent('as-button-click', {
    bubbles: true,
    composed: true,
    detail: { buttonName: 'save-and-close' },
  }));
};

export const handleSaveSession = (ctx: ProjectContext): void => {
  devlog('[SAVE] handleSaveSession: disable-session-handlers:', ctx.hasAttribute('disable-session-handlers'), '| pendingAction:', ctx.pendingAction);
  if (ctx.hasAttribute('disable-session-handlers')) {
    devlog('[SAVE] handleSaveSession: delegating via as-button-click (disable-session-handlers mode)');
    if (ctx.pendingAction === 'save-and-close') {
      delegateSaveToHost(ctx);
    }
    return;
  }

  devlog('[SAVE] handleSaveSession: simpleProjects:', ctx.simpleProjects, '| currentProject:', !!ctx.currentProject, '| name:', ctx.currentProject?.metadata.name ?? '(none)');

  if (!ctx.simpleProjects && (!ctx.currentProject || ctx.currentProject.metadata.name === 'Untitled')) {
    devlog('[SAVE] handleSaveSession: opening save-project modal (name required)');
    openSaveProjectModal(ctx);
  } else if (ctx.currentProject) {
    devlog('[SAVE] handleSaveSession: calling saveCurrentProject');
    saveCurrentProject(ctx);
  } else {
    devlog('[SAVE] handleSaveSession: no currentProject, nothing to save');
  }
};

export const handleRestoreSession = (ctx: ProjectContext): void => {
  if (ctx.hasAttribute('disable-session-handlers')) return;
  if (ctx.simpleProjects) return;
  window.dispatchEvent(new CustomEvent('as-inspector-open'));
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent('as-flash-element', { detail: { target: 'as-projects-list' } }));
  }, 300);
};

export const handleProjectSelect = async (ctx: ProjectContext, event: Event): Promise<void> => {
  const { projectId } = (event as CustomEvent).detail;
  const project = await getProjectById(projectId);
  if (!project) return;
  if (project.metadata.appId !== ctx.appId) {
    console.warn(`[LOAD] Refusing to load project for app "${project.metadata.appId}" into app "${ctx.appId}"`);
    window.dispatchEvent(new CustomEvent('as-session-restore-failed', {
      detail: { reason: 'app-id-mismatch', expectedAppId: project.metadata.appId },
    }));
    ctx.dispatchEvent(new CustomEvent('as-notify', {
      detail: { defaultText: `This .asproject file is meant for the ${project.metadata.appId} app!`, type: 'error', persist: true },
    }));
    return;
  }
  await loadProject(ctx, project);
};

export const handleProjectSave = async (ctx: ProjectContext, event: Event): Promise<void> => {
  const { projectId, projectName } = (event as CustomEvent).detail;
  if (!ctx.currentProject || ctx.currentProject.metadata.id !== projectId) return;
  const nameToUse = projectName || ctx.currentProject.metadata.name;
  if (nameToUse === 'Untitled') {
    window.dispatchEvent(new CustomEvent('as-project-save-error', { detail: { projectId, error: 'untitled' } }));
    return;
  }
  if (projectName && projectName !== ctx.currentProject.metadata.name) {
    ctx.currentProject.metadata.name = projectName;
  }
  await saveCurrentProject(ctx);
};

export const handleProjectRename = async (ctx: ProjectContext, event: Event): Promise<void> => {
  const { projectId, newName } = (event as CustomEvent).detail;
  devlog('[RENAME] Received rename event. ProjectId:', projectId, 'NewName:', newName);
  if (ctx.currentProject?.metadata.id === projectId) {
    ctx.currentProject.metadata.name = newName;
    ctx.currentProject.metadata.updatedAt = new Date();
    liveAppData.currentProject = ctx.currentProject;
  }
  const project = await getProjectById(projectId);
  if (project) {
    project.metadata.name = newName;
    project.metadata.updatedAt = new Date();
    await updateProject(project);
    await refreshProjectsList(ctx);
  }
};

export const handleProjectDelete = async (ctx: ProjectContext, event: Event): Promise<void> => {
  const { projectId } = (event as CustomEvent).detail;
  await deleteProject(projectId);
  if (ctx.currentProject?.metadata.id === projectId) createAndSetNewProject(ctx);
  await refreshProjectsList(ctx);
};

export const handleProjectLoadRequest = async (ctx: ProjectContext, event: Event): Promise<void> => {
  const { appId } = (event as CustomEvent).detail || {};
  const projects = await getProjectsForApp(appId || ctx.appId);
  ctx.dispatchEvent(new CustomEvent('as-projects-list', { bubbles: true, detail: { projects } }));
};

export const handleRestoreSessionFile = async (ctx: ProjectContext, event: Event): Promise<void> => {
  if (ctx.hasAttribute('disable-session-handlers')) return;

  const { blob } = (event as CustomEvent).detail ?? {};
  if (!blob) return;
  try {
    const project = await deserializeProject(blob);
    await loadProject(ctx, project);
  } catch (err) {
    ctx.logError('restore-file', err, 'regular');
    ctx.dispatchEvent(new CustomEvent('as-notify', {
      detail: { defaultText: 'Failed to load project file', type: 'error' },
    }));
  }
};

export const handleVisibilityChange = async (ctx: ProjectContext): Promise<void> => {
  const configuratorEl = ctx.querySelector<AsConfigurator>('as-configurator');

  if (document.visibilityState === 'hidden') {
    ctx.frozenAt = Date.now();
    if (!document.title.includes('(paused)')) document.title += ' (paused)';

    if (!configuratorEl?.initialized) return;
    configuratorEl.pauseSessionHeartbeat();

    // Autosave disabled — skip IDB snapshot on tab hide.
  } else {
    document.title = document.title.replace(' (paused)', '');
    const elapsed = ctx.frozenAt ? Date.now() - ctx.frozenAt : 0;
    ctx.frozenAt = null;

    const SESSION_TIMEOUT = 30 * 60 * 1000;
    const expired = elapsed > SESSION_TIMEOUT;
    if (expired && ctx.currentProject?.paramateSession && configuratorEl?.initialized) {
      handleSessionExpired(ctx, elapsed);
    }

    // Sync the interval baseline to prevent it from double-triggering
    // if the tab was suspended.
    lastSleepTick = Date.now();

    // Don't restart the keepalive when the session just expired — it would ping
    // the dead session id and 404. The expiry flow restarts it on the new session.
    if (!expired && configuratorEl?.initialized) configuratorEl.resetSessionTimout();
  }
};

let lastSleepTick = Date.now();
let sleepDetectionInterval: number | null = null;

export const initSleepDetection = (ctx: ProjectContext) => {
  if (sleepDetectionInterval) return;
  lastSleepTick = Date.now();
  sleepDetectionInterval = window.setInterval(() => {
    const now = Date.now();
    const elapsed = now - lastSleepTick;
    lastSleepTick = now;

    const SESSION_TIMEOUT = 30 * 60 * 1000;
    if (elapsed > SESSION_TIMEOUT) {
      handleSessionExpired(ctx, elapsed);
    }
  }, 10000);
};

export const teardownSleepDetection = () => {
  if (sleepDetectionInterval) {
    clearInterval(sleepDetectionInterval);
    sleepDetectionInterval = null;
  }
};

export const handleSessionExpired = (ctx: ProjectContext, elapsed: number) => {
  const configuratorEl = ctx.querySelector<AsConfigurator>('as-configurator');
  if (ctx.currentProject?.paramateSession && configuratorEl?.initialized) {
    // The server session is dead — stop the keepalive so nothing pings it (a 404
    // would surface as an error modal over the expiry flow). Restarted against
    // the fresh session by handleParamateInitialized after refreshSession/reinit.
    configuratorEl.pauseSessionHeartbeat();
    if (ctx.modalOverlay?.visibleChildId === 'expired-session') return;

    const modal = ctx.modalOverlay?.querySelector('#expired-session') as any;
    if (modal && typeof modal.setDate === 'function') {
      const saveDate = ctx.currentProject.metadata.updatedAt || new Date(Date.now() - elapsed);
      modal.setDate(saveDate);
    }
    ctx.openModal('expired-session', 'as-modal-expired-session');
  }
};
